import React, { useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { FiMinus, FiPlus, FiX } from 'react-icons/fi';
import type { MarketplaceDeliverableType, MarketplaceOrderDeliverable } from '@foodfluence/shared';
import {
  deliverableIsCarousel,
  deliverableLabel,
  MARKETPLACE_DELIVERABLE_TYPE_ORDER,
} from '@foodfluence/shared';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';

export type EditableLine = {
  id: string;
  type: MarketplaceDeliverableType;
  quantity: number;
  carouselPhotos: number;
};

export function newLineId() {
  return `dl_${Math.random().toString(36).slice(2, 9)}`;
}

export function toEditableLines(
  deliverables: Record<string, MarketplaceOrderDeliverable> | undefined
): EditableLine[] {
  if (!deliverables || Object.keys(deliverables).length === 0) {
    return [{ id: newLineId(), type: 'instagram_reel', quantity: 1, carouselPhotos: 5 }];
  }
  const grouped = new Map<string, EditableLine>();
  for (const d of Object.values(deliverables)) {
    const isCarousel = deliverableIsCarousel(d.type);
    const carouselPhotos = isCarousel ? (d.count ?? 5) : 5;
    const groupKey = isCarousel ? `${d.type}:${carouselPhotos}` : d.type;
    const existing = grouped.get(groupKey);
    if (existing) {
      existing.quantity += isCarousel ? 1 : (d.count ?? 1);
    } else {
      grouped.set(groupKey, {
        id: newLineId(),
        type: d.type,
        quantity: isCarousel ? 1 : (d.count ?? 1),
        carouselPhotos,
      });
    }
  }
  return [...grouped.values()];
}

export function linesToDeliverables(
  lines: EditableLine[]
): Record<string, MarketplaceOrderDeliverable> {
  const out: Record<string, MarketplaceOrderDeliverable> = {};
  for (const row of lines) {
    const quantity = Math.max(1, row.quantity);
    const isCarousel = deliverableIsCarousel(row.type);
    const slideCount = isCarousel ? Math.max(2, row.carouselPhotos) : undefined;
    for (let i = 0; i < quantity; i++) {
      const key = `${row.type}_${Math.random().toString(36).slice(2, 8)}`;
      out[key] = {
        id: key,
        type: row.type,
        ...(isCarousel ? { count: slideCount } : {}),
      };
    }
  }
  return out;
}

export function Stepper({
  value,
  min,
  onDecrement,
  onIncrement,
}: {
  value: number;
  min: number;
  onDecrement: () => void;
  onIncrement: () => void;
}) {
  return (
    <Flex align="center" bg={ff.grey} borderRadius="full" h="44px" flexShrink={0} fontFamily="display">
      <Box
        as="button"
        type="button"
        onClick={onDecrement}
        color={value <= min ? ff.faint : ff.ink}
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="40px"
        h="44px"
        flexShrink={0}
      >
        <Box as={FiMinus} boxSize="14px" strokeWidth={2.5} />
      </Box>
      <Text fontSize="15px" fontWeight="600" color={ff.ink} minW="20px" textAlign="center" flexShrink={0}>
        {value}
      </Text>
      <Box
        as="button"
        type="button"
        onClick={onIncrement}
        color={ff.ink}
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="40px"
        h="44px"
        flexShrink={0}
      >
        <Box as={FiPlus} boxSize="14px" strokeWidth={2.5} />
      </Box>
    </Flex>
  );
}

export function DeliverableRows({
  rows,
  onUpdate,
  onRemove,
}: {
  rows: EditableLine[];
  onUpdate: (id: string, patch: Partial<EditableLine>) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <>
      {rows.map((row) => (
        <Flex
          key={row.id}
          align="center"
          pl={4}
          pr={1}
          minH="60px"
          borderBottomWidth="1px"
          borderColor={ff.line}
          gap={2}
        >
          <Text fontSize="15px" fontWeight="600" color={ff.ink} flex={1} noOfLines={1}>
            {deliverableLabel(row.type)}
          </Text>

          {deliverableIsCarousel(row.type) ? (
            <Stepper
              value={row.carouselPhotos}
              min={2}
              onDecrement={() =>
                onUpdate(row.id, { carouselPhotos: Math.max(2, row.carouselPhotos - 1) })
              }
              onIncrement={() => onUpdate(row.id, { carouselPhotos: row.carouselPhotos + 1 })}
            />
          ) : (
            <Stepper
              value={row.quantity}
              min={1}
              onDecrement={() => onUpdate(row.id, { quantity: Math.max(1, row.quantity - 1) })}
              onIncrement={() => onUpdate(row.id, { quantity: row.quantity + 1 })}
            />
          )}

          <Box
            as="button"
            type="button"
            onClick={() => onRemove(row.id)}
            opacity={rows.length <= 1 ? 0.3 : 1}
            pointerEvents={rows.length <= 1 ? 'none' : undefined}
            color={ff.text2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="44px"
            h="44px"
            flexShrink={0}
          >
            <Box as={FiX} boxSize="16px" strokeWidth={2} />
          </Box>
        </Flex>
      ))}
    </>
  );
}

export function AddDeliverableRow({
  rows,
  onOpen,
}: {
  rows: EditableLine[];
  onOpen: () => void;
}) {
  const hasAvailable = useMemo(() => {
    const used = new Set(rows.map((r) => r.type));
    return (MARKETPLACE_DELIVERABLE_TYPE_ORDER as readonly MarketplaceDeliverableType[]).some(
      (t) => !used.has(t)
    );
  }, [rows]);

  if (!hasAvailable) return null;

  return (
    <Flex
      as="button"
      type="button"
      onClick={onOpen}
      align="center"
      px={4}
      minH="52px"
      borderBottomWidth="1px"
      borderColor={ff.line}
      gap={3}
      w="100%"
      textAlign="left"
      _hover={{ bg: ff.grey }}
    >
      <Flex w="24px" h="24px" borderRadius="full" bg={ff.ink} align="center" justify="center" flexShrink={0}>
        <Box as={FiPlus} boxSize="13px" color={ff.lime} strokeWidth={3} />
      </Flex>
      <Text fontSize="15px" fontWeight="600" color={ff.ink}>
        Add deliverable
      </Text>
    </Flex>
  );
}

export function DeliverablePickerSheet({
  isOpen,
  onClose,
  rows,
  onPick,
}: {
  isOpen: boolean;
  onClose: () => void;
  rows: EditableLine[];
  onPick: (type: MarketplaceDeliverableType) => void;
}) {
  const availableTypes = useMemo(() => {
    const used = new Set(rows.map((r) => r.type));
    return (MARKETPLACE_DELIVERABLE_TYPE_ORDER as readonly MarketplaceDeliverableType[]).filter(
      (t) => !used.has(t)
    );
  }, [rows]);

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Choose deliverable">
      <Box border="1px solid" borderColor={ff.line} borderRadius="20px" overflow="hidden">
        {availableTypes.map((t, idx) => (
          <Flex
            key={t}
            as="button"
            type="button"
            onClick={() => onPick(t)}
            align="center"
            px={4}
            h="52px"
            w="100%"
            textAlign="left"
            borderBottomWidth={idx < availableTypes.length - 1 ? '1px' : 0}
            borderColor={ff.line}
            _hover={{ bg: ff.grey }}
          >
            <Text fontSize="15px" fontWeight="600" color={ff.ink} flex={1}>
              {deliverableLabel(t)}
            </Text>
          </Flex>
        ))}
      </Box>
    </ModalShell>
  );
}
